import { ProductsFbtCollectionModel, fbtUploadFile } from "@NA/Services/ADIFbtUploadService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonProps } from "@insite/mobius/Button";
import FileUpload from "@insite/mobius/FileUpload";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import * as xlsx from "xlsx";

const mapStateToProps = (state: CustomApplicationState) => {
    const isAuthenticated = state.context.session.isAuthenticated;
    return {
        isAuthenticated,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface UploadFbtStyles {
    mainContainer?: InjectableCss;
    browseContainer?: InjectableCss;
    uploadContainer?: InjectableCss;
    formField?: InjectableCss;
    formButton?: InjectableCss;
    fileNameStyles?: TypographyProps;
    buttonDownload?: ButtonProps;
    uploadButton?: ButtonProps;
    invalidFileExtension?: TypographyProps;
    uploadPanel?: InjectableCss;
    uploadText?: InjectableCss;
    blueCircle?: InjectableCss;
    loader?: InjectableCss;
}

export const fbtStyles: UploadFbtStyles = {
    mainContainer: {
        css: css`
            margin: 0;
            padding-left: 20px;
            padding-right: 20px;
            border: 1px solid ${ADIColors.gray10};
            padding: 16px 20px;
            position: relative;
            width: 100%;
            &.mainContainer {
                opacity: 0.7;
            }
        `,
    },
    invalidFileExtension: {
        css: css`
            color: ${ADIColors.red1};
        `,
    },
    uploadPanel: {
        css: css`
            position: absolute;
            top: 55%;
            left: 50%;
            width: 100%;
            z-index: 99;
            transform: translate(-50%, -50%);
        `,
    },
    uploadText: {
        css: css`
            text-align: center;
        `,
    },
    loader: {
        css: css`
            margin: 0 auto;
            width: 100%;
        `,
    },
    blueCircle: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            position: relative;
            height: 42px;
            width: 42px;
            border-radius: 50%;
            background-color: #0c8dff;
            margin: 0 auto;
            &:after {
                content: "";
                position: absolute;
                display: block;
                left: 15px;
                top: 8px;
                width: 12px;
                height: 22px;
                border: solid ${ADIColors.white};
                border-width: 0 5px 5px 0;
                -webkit-transform: rotate(45deg);
                -ms-transform: rotate(45deg);
                transform: rotate(45deg);
            }
        `,
    },
    browseContainer: {
        css: css`
            min-height: 80px;
            display: flex;
            justify-items: center;
            gap: 5px;
        `,
    },
    uploadContainer: {
        css: css`
            display: flex;
            justify-content: end;
        `,
    },
    formButton: {
        css: css`
            height: 30px;
            min-height: auto !important;
            line-height: 20px;
            padding: 10px 27px;
            margin: 0;
            font-size: 14px;
            font-weight: bold;
            background-color: ${ADIColors.lightRed2} !important;
            color: ${ADIColors.white};
            text-align: center;
            float: left;
            border-radius: 0px;
            border: none;
            display: flex;
            justify-content: center;
            align-items: center;
            &:hover {
                opacity: 0.9;
            }
        `,
    },
    fileNameStyles: {
        css: css`
            padding-top: 5px;
            margin-left: 10px;
        `,
    },
    uploadButton: {
        css: css`
            height: 30px;
            min-height: auto !important;
            line-height: 20px;
            padding: 10px 27px;
            margin: 0;
            font-size: 14px;
            font-weight: bold;
            background-color: ${ADIColors.lightRed2};
            color: ${ADIColors.white};
            text-align: center;
            float: left;
            border-radius: 0px;
            border: none;
            display: flex;
            justify-content: center;
            align-items: center;
            &:disabled {
                background-color: ${ADIColors.gray11} !important;
                color: ${ADIColors.gray23};
                > span {
                    color: ${ADIColors.gray24} !important;
                }
                cursor: default;
            }
            &:hover {
                opacity: 0.9;
                background-color: ${ADIColors.lightRed2};
                border: none;
            }
        `,
    },
};

const styles = fbtStyles;

const ADIFbtUpload: FC<Props> = () => {
    const [filename, setFilename] = useState("No File selected.");
    const [file, setFile] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState(false);
    const [isValidFile, setIsValidFile] = useState(false);
    const [browsePanel, setBrowsePanel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [key, setKey] = useState(0);
    const firstRowHeading = false;

    const browseFbt = (event: any) => {
        if (event.target.files.length > 0) {
            const file = event.target.files[0];

            setFile(file);

            const fileExt = getFileExtention(file.name);
            const isValid = fileExt === "xls" || fileExt === "xlsx";
            setIsValidFile(isValid);

            if (fileExt === "xls" || fileExt === "xlsx") {
                setFilename(file.name);
                setErrorMessage(false);
            } else {
                setFilename(file.name);
                setErrorMessage(true);
            }

            setKey(prevKey => prevKey + 1);
        }
    };

    const getFileExtention = (fileName: string): string => {
        const splittedFileName: string[] = fileName.split(".");

        return splittedFileName.length > 0 ? splittedFileName[splittedFileName.length - 1].toLowerCase() : "";
    };

    const getFilename = (fileName: string): string => {
        const splittedFileNameNew: string = fileName;

        return splittedFileNameNew;
    };

    const handleUpload = () => {
        if (!file) {
            return;
        }
        let productsFbtCollection: ProductsFbtCollectionModel[] = [];

        const reader = new FileReader();
        const fileExt = getFileExtention(file.name);
        const fileExtname = getFilename(file.name);
        reader.onload = (evt: any) => {
            if (!evt) return;
            const bstr = evt.target.result;
            try {
                if (fileExt === "xls" || fileExt === "xlsx") {
                    const wb = xlsx.read(bstr, { type: "binary" });
                    setIsLoading(true);
                    if (wb) {
                        wb.SheetNames.forEach(sheetName => {
                            const opts = { header: 1 };
                            let roa = xlsx.utils.sheet_to_json(wb.Sheets[sheetName], opts);

                            if (roa.length > 0) {
                                if (firstRowHeading) {
                                    roa = roa.slice(1, roa.length);
                                }
                                productsFbtCollection = roa.map((r: any) => {
                                    const obj = {
                                        ADIPartNo: r[0],
                                        FBTProduct: r[1],
                                        FileName: fileExtname,
                                    };
                                    return obj;
                                });
                            } else {
                                setErrorMessage(true);
                                setIsValidFile(false);
                            }
                        });
                        if (productsFbtCollection.length > 0) {
                            fbtUploadFile(productsFbtCollection).then(() => setIsLoading(false));
                            setBrowsePanel(true);
                            setFilename("");
                            setTimeout(() => {
                                setBrowsePanel(false);
                                setFilename("No File Selected");
                            }, 5000);
                            setIsValidFile(false);
                        } else {
                            setErrorMessage(true);
                            setIsValidFile(false);
                        }
                    }
                }
            } catch (error) {
                return null;
            }
        };

        reader.readAsBinaryString(file);
    };

    return (
        <StyledWrapper className={browsePanel ? "mainContainer" : ""} {...styles.mainContainer}>
            <StyledWrapper {...styles.browseContainer}>
                <FileUpload
                    buttonText="Browse"
                    buttonProps={{ ...styles.formButton }}
                    onChange={browseFbt}
                    key={key}
                    cssOverrides={{
                        formField: css`
                            display: none;
                        `,
                    }}
                />
                <Typography {...styles.fileNameStyles}>{filename}</Typography>
                {errorMessage && (
                    <Typography {...styles.invalidFileExtension}>
                        {translate("File Not in Required Format.")}
                    </Typography>
                )}
            </StyledWrapper>

            {browsePanel && (
                <StyledWrapper {...styles.uploadPanel}>
                    {isLoading ? (
                        <StyledWrapper>
                            <LoadingSpinner {...styles.loader} />
                            <StyledWrapper {...styles.uploadText}>Upload Processing...</StyledWrapper>
                        </StyledWrapper>
                    ) : (
                        <>
                            <StyledWrapper {...styles.blueCircle}></StyledWrapper>
                            <StyledWrapper {...styles.uploadText}>File Upload Complete</StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.uploadContainer}>
                <StyledWrapper>
                    <Button
                        className={isValidFile ? "upload-button" : "disabled-button"}
                        disabled={!isValidFile}
                        {...styles.uploadButton}
                        onClick={handleUpload}
                    >
                        {translate("Upload")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ADIFbtUpload);
